import React from 'react';
import { FiCheckSquare, FiRefreshCw, FiUser, FiUsers } from 'react-icons/fi';
import type { CallConclusion } from './useCallConclusion';

interface CallConclusionFieldsProps {
  conclusion: CallConclusion;
  callNotesLoading?: boolean;
  onSkip?: () => void;
  skipLabel?: string;
  saveLabel?: string;
}

// Insured attach + call notes + AMS/Command Center task fields + Save/Skip footer —
// shared between the live post-call summary and the call-history detail panel.
const CallConclusionFields: React.FC<CallConclusionFieldsProps> = ({
  conclusion, callNotesLoading, onSkip, skipLabel = 'Skip', saveLabel = 'Save & Close',
}) => {
  const c = conclusion;

  return (
    <>
      {c.insuredId ? (
        <div className="dialer-selected-contact">
          <FiUser size={12} /> {c.contactName || 'Insured selected'}
        </div>
      ) : (
        <div className="call-summary__section">
          <div className="call-summary__section-header">
            Insured <span className="call-summary__required">*</span>
          </div>
          <div className="call-summary__insured-search">
            <input
              type="text"
              className="dialer-input"
              placeholder="Search insured to attach to this call…"
              value={c.insuredSearchTerm}
              onChange={(e) => c.setInsuredSearchTerm(e.target.value)}
            />
            {c.insuredSearchTerm.trim() !== '' && (
              <div className="call-summary__insured-dropdown">
                {c.insuredSearchLoading ? (
                  <div className="dialer-empty">Loading insureds…</div>
                ) : c.insuredSearchResults.length > 0 ? (
                  c.insuredSearchResults.map((insured, i) => (
                    <div
                      key={i}
                      className="dialer-contact-item"
                      onClick={() => c.onInsuredSelected(insured)}
                    >
                      <div className="dialer-contact-item__avatar"><FiUsers size={14} /></div>
                      <div className="dialer-contact-item__meta">
                        <span className="dialer-contact-item__name">{insured.name}</span>
                        <span className="dialer-contact-item__phone">{insured.phone}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dialer-empty">No insureds found</div>
                )}
              </div>
            )}
          </div>
          {c.insuredError && <p className="call-summary__error">{c.insuredError}</p>}
        </div>
      )}

      <div className="call-summary__section">
        <div className="call-summary__section-header">
          <FiCheckSquare size={13} /> Call Notes <span className="call-summary__required">*</span>
          {callNotesLoading && <FiRefreshCw size={12} className="dialer-spin" />}
        </div>
        <textarea
          className={`dialer-textarea call-summary__notes${c.callNotesError ? ' call-summary__notes--error' : ''}`}
          rows={5}
          value={c.callNotes}
          onChange={(e) => c.setCallNotes(e.target.value)}
          placeholder={callNotesLoading ? 'Generating notes…' : 'Enter call notes…'}
          disabled={!!callNotesLoading}
        />
        {c.callNotesError && <p className="call-summary__error">{c.callNotesError}</p>}

        <div className="call-summary__task-toggle">
          <input
            type="checkbox"
            id="dialer-create-reminder"
            checked={c.createTask}
            onChange={(e) => c.onCreateTaskToggle(e.target.checked)}
          />
          <label htmlFor="dialer-create-reminder">Create task in AMS</label>
        </div>
        <div className="call-summary__task-toggle">
          <input
            type="checkbox"
            id="dialer-create-cc-task"
            checked={c.createCommandCenterTask}
            onChange={(e) => c.setCreateCommandCenterTask(e.target.checked)}
          />
          <label htmlFor="dialer-create-cc-task">Create task in Command Center</label>
        </div>

        {(c.createTask || c.createCommandCenterTask) && (
          <div className="call-summary__task-form">
            <label htmlFor="dialer-reminder-task">Task</label>
            <textarea
              id="dialer-reminder-task"
              className={`dialer-textarea${c.taskError ? ' call-summary__notes--error' : ''}`}
              rows={3}
              placeholder="Enter task details…"
              value={c.reminderTask}
              onChange={(e) => c.setReminderTask(e.target.value)}
            />
            <label htmlFor="dialer-due-date">Due date</label>
            <input
              id="dialer-due-date"
              type="date"
              className="dialer-input"
              value={c.dueDate}
              onChange={(e) => c.setDueDate(e.target.value)}
            />
            {c.taskError && <p className="call-summary__error">{c.taskError}</p>}
          </div>
        )}
      </div>

      <div className="call-summary__footer">
        {onSkip && <button className="dialer-call-btn dialer-call-btn--secondary" onClick={onSkip}>{skipLabel}</button>}
        <button className="dialer-call-btn" onClick={() => c.save()} disabled={c.saving}>
          {c.saving ? <FiRefreshCw size={13} className="dialer-spin" /> : null}
          {c.saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </>
  );
};

export default CallConclusionFields;
